import { addTrailingSlash } from "@prismicio/editor-support/URL"
import { z } from "zod"

const stringToBoolean = z
  .enum(["true", "false"])
  .transform((value) => value === "true")

const stringToUrl = z
  .string()
  .transform((url, ctx) => {
    try {
      return new URL(url)
    } catch {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Invalid URL" })
      return z.NEVER
    }
  })
  .transform(addTrailingSlash)

const unset = z.literal("").transform(() => undefined)

type Env = z.infer<typeof Env>

const Env = z.object({
  AI_SERVICE_BASE_URL: stringToUrl,
  AI_SERVICE_WEBSOCKET_ENDPOINT: stringToUrl,
  AMPLITUDE_EXPERIMENT_SERVER_BASE_URL: stringToUrl,
  ASSETS_API_BASE_URL: stringToUrl,
  AUTHENTICATION_SERVER_BASE_URL: stringToUrl,
  CUSTOM_TYPES_API_BASE_URL: stringToUrl,
  DOCUMENT_SEARCH_BASE_URL: stringToUrl,
  EMBED_API_ENDPOINT: stringToUrl,
  INTEGRATION_FIELDS_API_BASE_URL: stringToUrl,
  REPOSITORY_BASE_URL: stringToUrl,
  UNSPLASH_API_BASE_URL: stringToUrl,
  USER_SERVICE_BASE_URL: stringToUrl,
  WROOM_BASE_URL: stringToUrl,

  INTERCOM_APP_ID: z.string().nonempty(),
  PAGE_BUILDER_AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY: z.string().nonempty(),
  PAGE_BUILDER_AUTH_STRATEGY: z.enum(["cookie", "token"]),
  PAGE_BUILDER_FALLBACK_REPOSITORY_NAME: z.string().nonempty().or(unset),
  PAGE_BUILDER_SENTRY_DSN: z.string().nonempty(),
  PAGE_BUILDER_SENTRY_ENABLED: stringToBoolean,
  PAGE_BUILDER_SENTRY_ENVIRONMENT: z.enum(["dev", "stage", "prod"]),

  VERCEL_URL: z.string().nonempty().optional(),
  PRISMIC_DOMAIN: z.string().nonempty(),
  PRISMIC_WROOM_BASE_URL: z.string().nonempty(),
  BASE_URL: z.string(),

  PRISMIC_GIT_COMMIT_HASH: z.string().nonempty().optional()
})

export const env = Env.parse({
  AI_SERVICE_BASE_URL: import.meta.env.PRISMIC_AI_SERVICE_BASE_URL,
  AI_SERVICE_WEBSOCKET_ENDPOINT: import.meta.env
    .PRISMIC_AI_SERVICE_WEBSOCKET_ENDPOINT,
  AMPLITUDE_EXPERIMENT_SERVER_BASE_URL: import.meta.env
    .PRISMIC_AMPLITUDE_EXPERIMENT_SERVER_BASE_URL,
  ASSETS_API_BASE_URL: import.meta.env.PRISMIC_ASSETS_API_BASE_URL,
  AUTHENTICATION_SERVER_BASE_URL: import.meta.env
    .PRISMIC_AUTHENTICATION_SERVER_BASE_URL,
  CUSTOM_TYPES_API_BASE_URL: import.meta.env.PRISMIC_CUSTOM_TYPES_API_BASE_URL,
  DOCUMENT_SEARCH_BASE_URL: import.meta.env.PRISMIC_DOCUMENT_SEARCH_BASE_URL,
  EMBED_API_ENDPOINT: import.meta.env.PRISMIC_EMBED_API_ENDPOINT,
  INTEGRATION_FIELDS_API_BASE_URL: import.meta.env
    .PRISMIC_INTEGRATION_FIELDS_API_BASE_URL,
  REPOSITORY_BASE_URL: import.meta.env.PRISMIC_REPOSITORY_BASE_URL,
  UNSPLASH_API_BASE_URL: import.meta.env.PRISMIC_UNSPLASH_API_BASE_URL,
  USER_SERVICE_BASE_URL: import.meta.env.PRISMIC_USER_SERVICE_BASE_URL,
  WROOM_BASE_URL: import.meta.env.PRISMIC_WROOM_BASE_URL,

  INTERCOM_APP_ID: import.meta.env.PRISMIC_INTERCOM_APP_ID,
  PAGE_BUILDER_AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY: import.meta.env
    .PRISMIC_PAGE_BUILDER_AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY,
  PAGE_BUILDER_AUTH_STRATEGY: import.meta.env
    .PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
  PAGE_BUILDER_FALLBACK_REPOSITORY_NAME: import.meta.env
    .PRISMIC_PAGE_BUILDER_FALLBACK_REPOSITORY_NAME,
  PAGE_BUILDER_SENTRY_DSN: import.meta.env.PRISMIC_PAGE_BUILDER_SENTRY_DSN,
  PAGE_BUILDER_SENTRY_ENABLED: import.meta.env
    .PRISMIC_PAGE_BUILDER_SENTRY_ENABLED,
  PAGE_BUILDER_SENTRY_ENVIRONMENT: import.meta.env
    .PRISMIC_PAGE_BUILDER_SENTRY_ENVIRONMENT,

  VERCEL_URL: import.meta.env.VITE_VERCEL_URL,
  PRISMIC_DOMAIN: import.meta.env.PRISMIC_DOMAIN,
  PRISMIC_WROOM_BASE_URL: import.meta.env.PRISMIC_WROOM_BASE_URL,
  BASE_URL: import.meta.env.BASE_URL,

  PRISMIC_GIT_COMMIT_HASH: import.meta.env.PRISMIC_GIT_COMMIT_HASH
} satisfies Record<keyof Env, string | undefined>)

export function getCoreApiBaseUrl(repositoryName: string): URL {
  const coreApiBaseUrl = new URL("core/", env.WROOM_BASE_URL)
  coreApiBaseUrl.hostname = `${repositoryName}.${coreApiBaseUrl.hostname}`
  return coreApiBaseUrl
}
